import React, { useEffect, useState, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, Target, DollarSign, Mail } from 'lucide-react';
import { Postit, PaperSlip, UrgentCard, PhoneMsg, WhatsAppGlyph } from './ChaosCards';
import './CRMTransition.css';

// --- DATA ---

const chaosItems = [
  { id:'c1',  type:'postit', color:'#fef08a', pinColor:'radial-gradient(circle at 35% 30%,#ff8a80,#c62828)', text:'Llamar\nmañana',           sub:'sin fecha',     float:'a', rot:-8  },
  { id:'c2',  type:'postit', color:'#fbcfe8', pinColor:'radial-gradient(circle at 35% 30%,#ffd180,#e65100)', text:'¿Respondido?',              sub:'ver email',     float:'b', rot:5   },
  { id:'c3',  type:'postit', color:'#bbf7d0', pinColor:'radial-gradient(circle at 35% 30%,#b9f6ca,#2e7d32)', text:'Volver a\ncontactar',       sub:'Javier T.',     float:'c', rot:-4  },
  { id:'c4',  type:'postit', color:'#e9d5ff', pinColor:'radial-gradient(circle at 35% 30%,#82b1ff,#1565c0)', text:'Pendiente\nseguimiento',    sub:'2 semanas',     float:'a', rot:7   },
  { id:'c5',  type:'postit', color:'#fed7aa', pinColor:'radial-gradient(circle at 35% 30%,#b9f6ca,#2e7d32)', text:'Lead\nnuevo',               sub:'sin asignar',   float:'b', rot:-6  },
  { id:'c6',  type:'postit', color:'#fecaca', pinColor:'radial-gradient(circle at 35% 30%,#ff8a80,#c62828)', text:'Urgente',                   sub:'sin respuesta', float:'c', rot:9   },
  { id:'c7',  type:'slip',   accentColor:'#6366F1', label:'Email recibido',  text:'"Interesado en el plan premium..."',          meta:'Laura V. — hace 3 días',        float:'a', rot:-3  },
  { id:'c8',  type:'slip',   accentColor:'#F59E0B', label:'Recordatorio',    text:'No olvidar reunión — jueves 3pm',             meta:'Sin confirmar',                 float:'b', rot:6   },
  { id:'c9',  type:'slip',   accentColor:'#10B981', label:'Seguimiento',     text:'Cliente pidió cotización — ¿se envió?',       meta:'Hace 5 días',                   float:'c', rot:-5  },
  { id:'c10', type:'urgent', text:'Cliente esperando respuesta',              time:'Hace 4 días · Sin respuesta',                                                        float:'a', rot:4   },
  { id:'c11', type:'urgent', text:'Agendar demo — contacto caliente',         time:'Ayer · Pendiente',                                                                   float:'b', rot:-7  },
  { id:'c12', type:'wa',     name:'Carlos M.', msg:'"Buenas, me interesa el servicio. ¿Info?"',              time:'Lunes 10:32 am',                                     float:'c', rot:3   },
  { id:'c13', type:'wa',     name:'Ana R.',    msg:'"¿Tienen disponibilidad para junio?"',                   time:'Hace 5 días',                                        float:'a', rot:-2  },
  { id:'c14', type:'postit', color:'#ccfbf1', pinColor:'radial-gradient(circle at 35% 30%,#ffd180,#e65100)', text:'Agendar\ndemo',             sub:'sin fecha',     float:'b', rot:11  },
  { id:'c15', type:'slip',   accentColor:'#EF4444', label:'Sin responder',   text:'WhatsApp — Pedro A. lleva 3 días esperando', meta:'Oportunidad perdida?',          float:'c', rot:-9  },
];

// Tarjetas puramente decorativas: solo llenan el tablero durante el caos
// (fase 0) y se desvanecen cuando empiezan a volar al pipeline — no tienen
// columna ni tarjeta limpia asociada, así que el pipeline no cambia.
const extraChaosItems = [
  { id:'x1',  type:'postit', color:'#fed7aa', pinColor:'radial-gradient(circle at 35% 30%,#ffd180,#e65100)', text:'Actualizar\nbase de datos', sub:'CRM',              rot:6   },
  { id:'x2',  type:'slip',   accentColor:'#8B5CF6', label:'Nota interna',    text:'Revisar contrato con Distribuidora Norte',    meta:'Pendiente firma',               rot:-4  },
  { id:'x3',  type:'urgent', text:'Cliente pidió reembolso',                  time:'Hoy · sin resolver',                                                                rot:8   },
  { id:'x4',  type:'postit', color:'#bbf7d0', pinColor:'radial-gradient(circle at 35% 30%,#b9f6ca,#2e7d32)', text:'Webinar\nRuray',            sub:'Próx. lunes',      rot:-9  },
  { id:'x5',  type:'wa',     name:'María G.',  msg:'"¿Siguen con la promo de junio?"',                       time:'Hace 2 días',                                        rot:5   },
  { id:'x6',  type:'slip',   accentColor:'#06B6D4', label:'Recordatorio',    text:'Cierre de mes — 30/05',                        meta:'',                              rot:-2  },
  { id:'x7',  type:'postit', color:'#fecaca', pinColor:'radial-gradient(circle at 35% 30%,#ff8a80,#c62828)', text:'Firma\ncontrato',           sub:'⚠️ pendiente',    rot:10  },
  { id:'x8',  type:'urgent', text:'Factura #204 sin enviar',                  time:'Hace 3 días',                                                                       rot:-6  },
  { id:'x9',  type:'wa',     name:'Diego P.',  msg:'"¿Tienen planes para clínicas grandes?"',                time:'Ayer',                                               rot:3   },
  { id:'x10', type:'postit', color:'#e9d5ff', pinColor:'radial-gradient(circle at 35% 30%,#82b1ff,#1565c0)', text:'Pendiente\nseguimiento',    sub:'hace 2 sem.',      rot:-11 },
];

const allChaosItems = [...chaosItems, ...extraChaosItems];

// Which pipeline column each chaos item belongs to (0=Nuevo,1=Contactado,2=Propuesta,3=Ganado)
const chaosToColumn = [1, 0, 1, 2, 0, 3, 1, 2, 2, 0, 3, 0, 0, 3, 1];

// The clean pipeline card each chaos item becomes after landing
const chaosToCard: { name:string; desc:string; usd:number; days:number; source:string }[] = [
  { name:'Javier Torres',      desc:'Volver a contactar',      usd:140, days:8,  source:'email' },
  { name:'Lead WhatsApp',      desc:'Sin asignar aún',          usd:120, days:1,  source:'wa'    },
  { name:'Laura V.',           desc:'Esperando propuesta',      usd:140, days:3,  source:'email' },
  { name:'Distribuidora Norte',desc:'Cotización enviada',        usd:320, days:6,  source:'email' },
  { name:'Ana R.',             desc:'¿Disponibilidad junio?',   usd:200, days:5,  source:'wa'    },
  { name:'Cliente Activo',     desc:'Onboarding completo',       usd:200, days:15, source:'wa'    },
  { name:'Laura V.',           desc:'Esperando propuesta',       usd:140, days:3,  source:'email' },
  { name:'Distribuidora Norte',desc:'Cotización enviada',        usd:320, days:6,  source:'email' },
  { name:'Distribuidora Norte',desc:'Cotización enviada',        usd:320, days:6,  source:'email' },
  { name:'Carlos M.',          desc:'Interés en plan básico',    usd:150, days:2,  source:'wa'    },
  { name:'Pedro A.',           desc:'Plan premium activo',       usd:299, days:19, source:'wa'    },
  { name:'Carlos M.',          desc:'Interés en plan básico',    usd:150, days:2,  source:'wa'    },
  { name:'Ana R.',             desc:'¿Disponibilidad junio?',    usd:200, days:5,  source:'wa'    },
  { name:'Pedro A.',           desc:'Plan premium activo',       usd:299, days:19, source:'wa'    },
  { name:'Javier Torres',      desc:'Volver a contactar',        usd:140, days:8,  source:'email' },
];

const pipelineColumns = [
  { id:'nuevo',     label:'Nuevo',      color:'#06B6D4', deals:3, usd:470  },
  { id:'contactado',label:'Contactado', color:'#7C3AED', deals:2, usd:280  },
  { id:'propuesta', label:'Propuesta',  color:'#F59E0B', deals:1, usd:320  },
  { id:'ganado',    label:'Ganado',     color:'#10B981', deals:2, usd:499  },
];

const dashMetrics = [
  { label:'Leads activos',  value:23,   suffix:'',    color:'#06B6D4' },
  { label:'Tasa de cierre', value:68,   suffix:'%',   color:'#7C3AED' },
  { label:'Pipeline total', value:1709, suffix:'USD', color:'#10B981' },
];

const dashFeed = [
  { icon:'wa',    text:'WhatsApp respondido', name:'Carlos M.',           time:'hace 2 min'  },
  { icon:'check', text:'Demo agendada',        name:'Ana R.',              time:'hace 15 min' },
  { icon:'send',  text:'Cotización enviada',   name:'Distribuidora Norte', time:'hace 1h'     },
  { icon:'star',  text:'Deal ganado',           name:'Pedro A.',            time:'hoy'         },
];

const barData   = [40,55,48,70,62,85,91];
const barLabels = ['Ene','Feb','Mar','Abr','May','Jun','Jul'];

// Geometría de la gráfica de área "Leads por mes" (se calcula una sola vez:
// barData/barLabels son constantes estáticas).
const CHART_W = 280;
const CHART_H = 92;
const chartPoints = barData.map((v, i) => ({
  x: (i / (barData.length - 1)) * CHART_W,
  y: CHART_H - (v / 100) * CHART_H,
}));
const chartLinePath = chartPoints.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');
const chartAreaPath = `${chartLinePath} L ${CHART_W} ${CHART_H} L 0 ${CHART_H} Z`;
const chartLastPoint = chartPoints[chartPoints.length - 1];

// --- PIPELINE CARD (clean version shown after landing) ---
interface PipelineCardData { name:string; desc:string; usd:number; days:number; source:string }
const PipelineCardClean: React.FC<{ card:PipelineCardData; color:string; isLight:boolean }> = ({ card, color, isLight }) => {
  const initials = card.name
    .split(' ')
    .map((p: string) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <div style={{
      position:'relative', display:'flex', alignItems:'flex-start', gap:'8px',
      background: isLight?'#fff':'rgba(255,255,255,0.04)',
      borderRadius:'11px', padding:'9px 9px',
      border:`1px solid ${isLight?'rgba(0,0,0,0.05)':'rgba(255,255,255,0.07)'}`,
      boxShadow: isLight?'0 1px 3px rgba(15,23,42,0.05)':'none',
      width:'100%', boxSizing:'border-box' as const,
    }}>
      <div style={{
        width:'22px', height:'22px', borderRadius:'50%', flexShrink:0, marginTop:'1px',
        display:'flex', alignItems:'center', justifyContent:'center',
        fontSize:'9px', fontWeight:800, color:'#fff',
        background:`linear-gradient(135deg, ${color}, ${color}bb)`,
      }}>
        {initials}
      </div>

      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ display:'flex', alignItems:'baseline', justifyContent:'space-between', gap:'6px' }}>
          <span style={{ fontSize:'12px', fontWeight:700, color:isLight?'#1e1b4b':'#f1f5f9', lineHeight:1.3, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
            {card.name}
          </span>
          <span style={{ fontSize:'11px', fontWeight:800, color, letterSpacing:'-0.01em', flexShrink:0 }}>
            ${card.usd}
          </span>
        </div>
        <div style={{ fontSize:'10px', color:isLight?'rgba(0,0,0,0.4)':'rgba(255,255,255,0.38)', margin:'3px 0 8px', lineHeight:1.3, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
          {card.desc}
        </div>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div style={{ width:'15px', height:'15px', borderRadius:'5px', flexShrink:0, background:card.source==='wa'?'#25D366':'#6366F1', display:'flex', alignItems:'center', justifyContent:'center' }}>
            {card.source==='wa'
              ? <WhatsAppGlyph size={9} />
              : <Mail size={9} color="#fff" strokeWidth={2.5} />}
          </div>
          <span style={{ fontSize:'9.5px', color:isLight?'rgba(0,0,0,0.3)':'rgba(255,255,255,0.28)', display:'flex', alignItems:'center', gap:'3px', flexShrink:0 }}>
            <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
            {card.days}d
          </span>
        </div>
      </div>
    </div>
  );
};

// --- FEED ICON ---
const FeedIcon: React.FC<{ type:string }> = ({ type }) => {
  const base = { width:'23px', height:'23px', borderRadius:'7px', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 as const, boxShadow:'0 2px 6px rgba(0,0,0,0.15)' };
  if (type==='wa')    return <div style={{ ...base, background:'#25D366' }}><WhatsAppGlyph size={11} /></div>;
  if (type==='check') return <div style={{ ...base, background:'#7C3AED' }}><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg></div>;
  if (type==='send')  return <div style={{ ...base, background:'#F59E0B' }}><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg></div>;
  return <div style={{ ...base, background:'#10B981' }}><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg></div>;
};

// --- MAIN COMPONENT ---

interface CRMTransitionProps {
  onPhaseChange?: (phase: 0|1|2|3) => void;
}

const CRMTransition: React.FC<CRMTransitionProps> = ({ onPhaseChange }) => {
  // phase 0=chaos  1=flying+pipeline building  2=pipeline settled  3=dashboard
  const [phase, setPhase]           = useState<0|1|2|3>(0);
  const [inView, setInView]         = useState(false);
  const [dimensions, setDimensions] = useState({ w:0, h:0, cx:0, cy:0 });
  const [landedItems, setLandedItems] = useState<Set<number>>(new Set());
  const [metricValues, setMetricValues] = useState([0,0,0]);
  const [barProgress,  setBarProgress]  = useState(0);
  const [visibleFeed,  setVisibleFeed]  = useState(0);

  const colRefs  = useRef<(HTMLDivElement|null)[]>([null,null,null,null]);
  const sceneRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    onPhaseChange?.(phase);
  }, [phase, onPhaseChange]);

  useEffect(() => {
    const resize = () => {
      const el = document.getElementById('scene-root');
      if (el){ const w=el.offsetWidth,h=el.offsetHeight; setDimensions({ w,h,cx:w/2,cy:h/2 }); }
    };
    resize();
    window.addEventListener('resize',resize);
    return ()=>window.removeEventListener('resize',resize);
  },[]);

  // Solo corre la secuencia mientras la sección está realmente visible, y
  // reinicia desde el caos (fase 0) cada vez que el usuario vuelve a llegar a ella.
  useEffect(() => {
    const el = sceneRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold: 0.3 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const animateValue = (target:number, setter:(v:number)=>void, duration=1200) => {
    const t0 = performance.now();
    const tick = (now:number) => {
      const t = Math.min((now-t0)/duration,1);
      setter(Math.round((1-Math.pow(1-t,3))*target));
      if (t<1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  };

  // Pixel targets for each column (center-x, top of card area)
  const [colTargets, setColTargets] = useState<{x:number;y:number}[]>([]);

  useEffect(() => {
    let active = true;

    // Solo se usa dentro de este efecto — vive aquí para que ESLint no exija
    // agregarla a las deps (agregarla reiniciaría toda la secuencia de ~20s
    // cada vez que cambia colTargets/phase/etc, ya que ella misma los dispara).
    const computeColTargets = () => {
      const scene = sceneRef.current;
      if (!scene) return;
      const sr = scene.getBoundingClientRect();
      // Count how many items are already assigned per column so we can stack vertically
      const perColCount = [0,0,0,0];
      const targets = chaosItems.map((_,i) => {
        const ci = chaosToColumn[i];
        const el = colRefs.current[ci];
        if (!el) return { x: dimensions.cx - 55, y: dimensions.cy };
        const r = el.getBoundingClientRect();
        const cardH = 62; // approximate height of each pipeline card + gap
        const stackOffset = perColCount[ci] * cardH;
        perColCount[ci]++;
        return {
          x: r.left - sr.left + 8,          // left edge of column + padding
          y: r.top  - sr.top  + 68 + stackOffset  // below header+USD, stacked
        };
      });
      setColTargets(targets);
    };

    const seq = async () => {
      if (!active) return;
      setPhase(0);
      setLandedItems(new Set());
      setMetricValues([0,0,0]);
      setBarProgress(0);
      setVisibleFeed(0);
      setColTargets([]);

      // Show chaos
      await new Promise(r=>setTimeout(r,3400));
      if (!active) return;

      // Pipeline panel appears, then cards start flying one by one
      setPhase(1);
      await new Promise(r=>setTimeout(r,120)); // wait for panel to render + refs
      computeColTargets();

      // Land each item with stagger
      for (let i=0;i<chaosItems.length;i++){
        await new Promise(r=>setTimeout(r,160));
        if (!active) return;
        setLandedItems(prev=>new Set([...prev,i]));
      }

      await new Promise(r=>setTimeout(r,700));
      if (!active) return;
      setPhase(2);

      // Dashboard
      await new Promise(r=>setTimeout(r,4500));
      if (!active) return;
      setPhase(3);
      animateValue(23,   v=>setMetricValues(p=>{const n=[...p];n[0]=v;return n;}));
      animateValue(68,   v=>setMetricValues(p=>{const n=[...p];n[1]=v;return n;}),1000);
      animateValue(1709, v=>setMetricValues(p=>{const n=[...p];n[2]=v;return n;}),1400);
      setTimeout(()=>{ if(active) setBarProgress(1); },400);
      dashFeed.forEach((_,i)=>setTimeout(()=>{ if(active) setVisibleFeed(i+1); },800+i*350));

      await new Promise(r=>setTimeout(r,7000));
      if (!active) return;
      seq();
    };

    if (dimensions.w>0 && inView) seq();
    return ()=>{ active=false; };
  },[dimensions.w, dimensions.cx, dimensions.cy, inView]);

  // Chaos item resting positions — one card per cell of a scrambled grid,
  // each nudged by a random jitter. This guarantees full coverage of the
  // board (corners, edges, center) instead of a ring/spiral that leaves
  // the middle and corners empty.
  const GRID_COLS = 5;
  const GRID_ROWS = 5;
  const seededRandom = (seed: number) => {
    const x = Math.sin(seed * 12.9898) * 43758.5453;
    return x - Math.floor(x);
  };
  // Huella máxima estimada de una tarjeta (la más ancha/alta de todas las
  // variantes), usada para que el jitter nunca empuje una tarjeta fuera
  // del tablero, sin importar cuál le toque a esa celda.
  const CARD_W = 180;
  const CARD_H = 150;

  const itemPositions = useMemo(()=>{
    const { w, h } = dimensions;
    const cellW = w / GRID_COLS;
    const cellH = h / GRID_ROWS;
    const maxX = Math.max(w - CARD_W, 0);
    const maxY = Math.max(h - CARD_H, 0);
    return allChaosItems.map((_,i)=>{
      // 7 y 25 (GRID_COLS*GRID_ROWS) son coprimos, así que esto revuelve en
      // qué celda cae cada tarjeta sin repetir ninguna celda.
      const cell = (i * 7 + 3) % (GRID_COLS * GRID_ROWS);
      const col = cell % GRID_COLS;
      const row = Math.floor(cell / GRID_COLS);
      const jitterX = (seededRandom(i * 2 + 1) - 0.5) * cellW * 0.6;
      const jitterY = (seededRandom(i * 2 + 2) - 0.5) * cellH * 0.6;
      const x = col * cellW + cellW / 2 + jitterX - CARD_W / 2;
      const y = row * cellH + cellH / 2 + jitterY - CARD_H / 2;
      return {
        x: Math.min(Math.max(x, 0), maxX),
        y: Math.min(Math.max(y, 0), maxY),
      };
    });
  },[dimensions]);

  // Las tarjetas se pueden arrastrar libremente mientras están en el tablero de caos
  const dragProps = {
    drag: true,
    dragConstraints: sceneRef,
    dragElastic: 0,
    dragMomentum: false,
    dragTransition: { bounceStiffness: 600, bounceDamping: 20 },
    whileDrag: { scale: 1.1, zIndex: 999, cursor: 'grabbing', boxShadow: '0 20px 50px rgba(0,0,0,0.5)' },
    whileHover: { scale: 1.05, zIndex: 200 },
  };

  // Track how many items have landed per column (to stack cards)
  const landedPerCol = useMemo(()=>{
    const counts = [0,0,0,0];
    [...landedItems].sort((a,b)=>a-b).forEach(i=>counts[chaosToColumn[i]]++);
    return counts;
  },[landedItems]);

  return (
    <div
      id="scene-root"
      ref={sceneRef}
      style={{ position:'relative', width:'100%', height:'100%', background:'transparent', overflow:'visible', display:'flex', alignItems:'center', justifyContent:'center' }}
    >
      {/* ── CORK BOARD BACKDROP (phase 0 only) ── */}
      <AnimatePresence>
        {phase===0 && (
          <motion.div
            key="corkboard"
            className="board"
            initial={{ opacity:0 }}
            animate={{ opacity:1 }}
            exit={{ opacity:0 }}
            transition={{ duration:0.4 }}
            style={{ position:'absolute', inset:14, zIndex:1 }}
          />
        )}
      </AnimatePresence>

      {/* ── PIPELINE PANEL (phases 1 & 2) ── */}
      <AnimatePresence>
        {(phase===1||phase===2) && (
          <motion.div
            key="pipeline"
            initial={{ opacity:0, scale:0.94, y:18 }}
            animate={{ opacity:1, scale:1, y:0 }}
            exit={{ opacity:0, scale:0.95, y:-14 }}
            transition={{ duration:0.5, ease:[0.16,1,0.3,1] }}
            className="crmt-panel"
            style={{ zIndex:6 }}
          >
            {/* Header */}
            <div className="crmt-header">
              <div className="crmt-header-icon">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/>
                </svg>
              </div>
              <div>
                <div className="crmt-header-title">Ruray CRM</div>
                <div className="crmt-header-subtitle">Pipeline comercial</div>
              </div>
              <div className="crmt-live-badge">
                <div className="crmt-live-dot" />
                <span className="crmt-live-label">En vivo</span>
              </div>
            </div>

            {/* Columns — centradas en el alto disponible en vez de estirarse
                y dejar un vacío debajo de las últimas tarjetas */}
            <div className="crmt-columns-wrap">
              <div className="crmt-columns-row">
                {pipelineColumns.map((col,ci)=>(
                  <div
                    key={col.id}
                    ref={el=>{ colRefs.current[ci]=el; }}
                    className="crmt-column"
                  >
                    {/* Column header */}
                    <div className="crmt-column-header">
                      <span className="crmt-column-label">{col.label}</span>
                      <motion.span
                        animate={{ opacity:1 }}
                        style={{ fontSize:'9px', fontWeight:800, color:'#fff', background:col.color, borderRadius:'999px', padding:'2px 7px', minWidth:'16px', textAlign:'center' }}
                      >
                        {landedPerCol[ci]}
                      </motion.span>
                    </div>

                    {/* USD total — animates as items land */}
                    <div className="crmt-usd-block">
                      <div className="crmt-usd-label">
                        Valor total
                      </div>
                      <div className="crmt-usd-value" style={{ color:col.color }}>
                        USD {col.usd.toLocaleString()}
                      </div>
                    </div>

                    {/* Landed cards appear here */}
                    <div className="crmt-cards-list">
                      <AnimatePresence>
                        {chaosItems.map((_, i) => {
                          if (chaosToColumn[i]!==ci) return null;
                          if (!landedItems.has(i)) return null;
                          const card = chaosToCard[i];
                          return (
                            <motion.div
                              key={`landed-${i}`}
                              initial={{ opacity:0, scale:0.75, y:-10 }}
                              animate={{ opacity:1, scale:1, y:0 }}
                              transition={{ duration:0.35, ease:[0.34,1.56,0.64,1] }}
                            >
                              <PipelineCardClean card={card} color={col.color} isLight={false} />
                            </motion.div>
                          );
                        })}
                      </AnimatePresence>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── CHAOS ITEMS — float in phase 0, fly to column in phase 1 ── */}
      <div style={{ position:'absolute', inset:0, pointerEvents:'none', zIndex:10 }}>
        <AnimatePresence>
          {(phase===0||phase===1) && allChaosItems.map((item,i)=>{
            const isExtra = i >= chaosItems.length;

            // Las tarjetas decorativas solo existen en el caos: en cuanto
            // empieza el vuelo al pipeline, desaparecen (con su exit) en vez
            // de volar, porque no tienen columna/tarjeta asignada.
            if (isExtra && phase===1) return null;

            const pos    = itemPositions[i];
            const colIdx = isExtra ? -1 : chaosToColumn[i];
            const target = isExtra ? undefined : colTargets[colIdx];
            const landed = isExtra ? false : landedItems.has(i);

            // Once landed we hide the flying chaos card (the pipeline card inside column shows instead)
            if (landed) return null;

            const isFlying = !isExtra && phase===1 && colTargets.length>0;

            return (
              <motion.div
                key={item.id}
                className="chaos-item"
                {...(phase===0 ? dragProps : {})}
                initial={{ opacity:0, x: pos.x, y: -160, scale:0.8, rotate: item.rot }}
                animate={{
                  opacity: 1,
                  scale:   isFlying ? 0.55 : 1,
                  x:       isFlying && target ? target.x : pos.x,
                  y:       isFlying && target ? target.y : pos.y,
                  rotate:  isFlying ? 0 : item.rot,
                }}
                exit={{ opacity:0, scale:0.85, transition:{ duration:0.3 } }}
                transition={{
                  duration: isFlying ? 0.55 : 0.6,
                  delay:    isFlying ? i*0.04 : i*0.08,
                  ease:     isFlying ? [0.4,0,0.2,1] : [0.34,1.56,0.64,1],
                  opacity:  { duration:0.25, delay: isFlying ? i*0.04 : i*0.08 }
                }}
                style={{
                  position:'absolute', top:0, left:0, zIndex:10,
                  willChange:'transform,opacity',
                  pointerEvents: phase===0 ? 'auto' : 'none',
                  touchAction: 'none',
                }}
              >
                {item.type==='postit'  && <Postit color={item.color!} pinColor={item.pinColor!} text={item.text!} sub={item.sub} />}
                {item.type==='slip'    && <PaperSlip accentColor={item.accentColor!} label={item.label!} text={item.text!} meta={item.meta} />}
                {item.type==='urgent'  && <UrgentCard text={item.text!} time={item.time!} />}
                {item.type==='wa'      && <PhoneMsg name={item.name!} text={item.msg!} time={item.time!} />}
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>

      {/* ── DASHBOARD (phase 3) ── */}
      <AnimatePresence>
        {phase===3 && (
          <motion.div
            key="dashboard"
            initial={{ opacity:0, scale:0.93, y:18 }}
            animate={{ opacity:1, scale:1, y:0 }}
            exit={{ opacity:0, scale:0.95, y:-14 }}
            transition={{ duration:0.55, ease:[0.16,1,0.3,1] }}
            className="crmt-panel"
            style={{ zIndex:12, padding:'20px 18px 16px' }}
          >
            {/* Header */}
            <motion.div initial={{opacity:0,y:-10}} animate={{opacity:1,y:0}} transition={{duration:0.4,delay:0.05}}
              className="crmt-header" style={{ marginBottom:'18px' }}>
              <div className="crmt-header-icon">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/>
                </svg>
              </div>
              <div>
                <div className="crmt-header-title">Ruray CRM</div>
                <div className="crmt-header-subtitle">Panel de control</div>
              </div>
              <div className="crmt-date-badge">
                Julio 2025
              </div>
            </motion.div>

            {/* Metrics */}
            <div className="crmt-metrics-row">
              {dashMetrics.map((m,i)=>{
                const Icon = [Users, Target, DollarSign][i];
                const caption = [
                  'Leads en seguimiento',
                  'Últimos 30 días',
                  'Valor potencial abierto',
                ][i];
                return (
                  <motion.div key={i}
                    initial={{opacity:0,y:16}} animate={{opacity:1,y:0}}
                    transition={{duration:0.45,delay:0.1+i*0.09,ease:[0.16,1,0.3,1]}}
                    className="crmt-metric-card">
                    <div className="crmt-metric-top">
                      <div className="crmt-metric-icon" style={{ background:m.color }}>
                        <Icon size={17} color="#fff" strokeWidth={2.2} />
                      </div>
                      <span className="crmt-metric-label">
                        {m.label}
                      </span>
                    </div>

                    <div className="crmt-metric-value">
                      {m.suffix==='USD'?`$${metricValues[i].toLocaleString()}`:`${metricValues[i]}${m.suffix}`}
                    </div>

                    <div className="crmt-metric-caption">
                      {caption}
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* Chart + Feed */}
            <div className="crmt-chart-feed-row">
              <motion.div initial={{opacity:0,x:-16}} animate={{opacity:1,x:0}} transition={{duration:0.45,delay:0.3}}
                className="crmt-chart-panel">
                <div className="crmt-chart-head">
                  <span className="crmt-panel-eyebrow">Leads por mes</span>
                  <span className="crmt-chart-total">{barData[barData.length-1]}</span>
                </div>
                <div className="crmt-chart-svg-wrap">
                  <svg viewBox={`0 0 ${CHART_W} ${CHART_H}`} preserveAspectRatio="none" style={{ width:'100%', height:'100%', display:'block' }}>
                    <defs>
                      <linearGradient id="leadsFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#06B6D4" stopOpacity="0.4" />
                        <stop offset="100%" stopColor="#06B6D4" stopOpacity="0" />
                      </linearGradient>
                    </defs>
                    {[0.25,0.5,0.75].map(t=>(
                      <line key={t} x1="0" x2={CHART_W} y1={CHART_H*t} y2={CHART_H*t} stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
                    ))}
                    <motion.path
                      d={chartAreaPath}
                      fill="url(#leadsFill)"
                      initial={{ opacity:0 }}
                      animate={{ opacity:barProgress?1:0 }}
                      transition={{ duration:0.6, delay:0.5 }}
                    />
                    <motion.path
                      d={chartLinePath}
                      fill="none" stroke="#06B6D4" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke"
                      initial={{ pathLength:0 }}
                      animate={{ pathLength:barProgress?1:0 }}
                      transition={{ duration:1.1, delay:0.4, ease:[0.16,1,0.3,1] }}
                    />
                    <motion.circle
                      cx={chartLastPoint.x} cy={chartLastPoint.y} r="4.5"
                      fill="#06B6D4" stroke="#0a0a0a" strokeWidth="2" vectorEffect="non-scaling-stroke"
                      initial={{ opacity:0, scale:0 }}
                      animate={{ opacity:barProgress?1:0, scale:barProgress?1:0 }}
                      transition={{ duration:0.3, delay:1.4 }}
                    />
                  </svg>
                </div>
                <div className="crmt-chart-labels">
                  {barLabels.map((l,i)=>(
                    <span key={l} style={{ fontSize:'8px', fontWeight: i===barLabels.length-1?800:600, color: i===barLabels.length-1?'#f1f5f9':'rgba(255,255,255,0.28)' }}>{l}</span>
                  ))}
                </div>
              </motion.div>

              <motion.div initial={{opacity:0,x:16}} animate={{opacity:1,x:0}} transition={{duration:0.45,delay:0.35}}
                className="crmt-feed-panel">
                <div className="crmt-feed-title">Actividad reciente</div>
                <div className="crmt-feed-list">
                  <AnimatePresence>
                    {dashFeed.slice(0,visibleFeed).map((f,i,arr)=>(
                      <motion.div key={i}
                        initial={{opacity:0,x:12}} animate={{opacity:1,x:0}}
                        transition={{duration:0.35,ease:[0.16,1,0.3,1]}}
                        className="crmt-feed-item">
                        {i < arr.length-1 && (
                          <span className="crmt-feed-connector" />
                        )}
                        <FeedIcon type={f.icon} />
                        <div className="crmt-feed-text">
                          <div className="crmt-feed-item-title">{f.text}</div>
                          <div className="crmt-feed-item-meta">{f.name} <span style={{opacity:0.55}}>·</span> {f.time}</div>
                        </div>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
};

export default CRMTransition;
